import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { readFormId, withForm, formBelongsToCompany } from '../utils/wizardForm';
import { loadStripe } from '@stripe/stripe-js';
import IrsMaintenanceBanner from "./IrsMaintenanceBanner";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';


const stripePromise = loadStripe( 
  'pk_live_51QFkHWDxSnLolRmeUoubokLeso1STh2OklVe5rzLwVR87JZUwkhdgous1ePR1SxzRXRGcLbLmklnscUdMa3i1znz00Zcc28Jqm'
  //'pk_test_51QX3fKG8KGwvZ4lzrjAvbwHlayyXgDFYzjdtYfpqRdtwtPcQAsJGrVOWmdxbAspafJ4EYAPL5ijXOlLErtC0cZz300y2blY02q'
);
function CheckoutForm({ clientSecret, consent, onConsentChange, onSuccessNavigate, formId: wizardFormId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const { id: formId } = useParams();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) {
      setError('You must agree to the disclosure before submitting.');
      return;
    }
    if (!stripe || !elements) return;

    setProcessing(true);
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${withForm(`/company/${formId}/amended-return/irs-confirmation`, wizardFormId)}`
      },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccessNavigate();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-6 py-4">
      <PaymentElement />
      {error && <p className="mt-2 text-red-600 text-sm">{error}</p>}

      <div className="flex items-start space-x-2 mt-4">
        <input
          type="checkbox"
          id="consent"
          className="mt-1 h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
          checked={consent}
          onChange={e => onConsentChange(e.target.checked)}
        />
        <label htmlFor="consent" className="text-sm">
          I agree to the disclosure on Form 2290 and Schedule 1 / Form 8849.{' '}
          <a href={`/company/${formId}/file-form/disclosure`} className="text-blue-600 underline hover:text-blue-800">
            Read disclosure terms
          </a>
        </label>
      </div>

      <button
        type="submit"
        disabled={!stripe || processing}
        className="mt-6 bg-[#0F2C5E] text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? 'Processing…' : 'Pay & Submit'}
      </button>
    </form>
  );
}

export default function AmendedPaymentAndSubmission() {
  const { id: ID } = useParams();
  const navigate = useNavigate();
  // Форма и тип поправки — из адреса этой вкладки (см. utils/wizardForm.js)
  const [searchParams] = useSearchParams();
  const formId = readFormId(searchParams);
  const amendType = searchParams.get('type');
  const isVinCorrection = amendType === 'VIN';
  const [processingFee, setProcessingFee] = useState(0);
  const [clientSecret, setClientSecret] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submittingVin, setSubmittingVin] = useState(false);
  const [vinError, setVinError] = useState('');

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment & Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 5;

  useEffect(() => {
  let isMounted = true;

  const init = async () => {
    if (!isMounted) return; // отменяем если размонтировано
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!formId) throw new Error('Form ID not found');

      const res1 = await fetch(`/api/form2290/${formId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res1.ok) throw new Error('Error loading form data');
      const data = await res1.json();
      if (!formBelongsToCompany(data, ID)) {
        navigate(`/company/${ID}`, { replace: true });
        return;
      }
      const count = Array.isArray(data.vehicles) ? data.vehicles.length : 0;
      let fee;
      if (count === 1) fee = 19.99;
      else if (count <= 24) fee = 24.99;
      else if (count <= 100) fee = 34.99;
      else if (count <= 250) fee = 49.99;
      else if (count <= 1000) fee = 74.99;
      else fee = 99.99;

      setProcessingFee(fee);

      if (isVinCorrection) {
        return;
      }

      const res2 = await fetch(
        `/api/form2290/${formId}/create-payment-intent/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ processing_fee: fee })
        }
      );
      if (!res2.ok) throw new Error('Error creating payment intent');
      const { clientSecret } = await res2.json();
      setClientSecret(clientSecret);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  init();

  return () => {
    isMounted = false;
  };
}, [formId, isVinCorrection, ID, navigate]);

  // Назад — на ту страницу поправки, с которой пришли (со всем её контекстом в
  // адресе). Раньше кнопка всегда вела на смену веса, даже из VIN correction.
  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmitVinCorrection = async () => {
    setVinError('');
    if (!consent) {
      setVinError('You must agree to the disclosure before submitting.');
      return;
    }
    setSubmittingVin(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/form2290/${formId}/submit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit to IRS');
      }
      navigate(withForm(`/company/${ID}/amended-return/irs-confirmation`, formId));
    } catch (e) {
      setVinError(e.message);
    } finally {
      setSubmittingVin(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading payment form…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      <HeaderLogged />
      {/* Progress bar */}

      {/* Main content */}
      <main className="flex-1 py-6 px-4 flex justify-center">
        <div className="w-full max-w-4xl bg-white border border-gray-200 shadow-md">
          {/* Processing Fee (skipped for VIN correction) */}
          {!isVinCorrection && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y text-sm">
              <thead>
                <tr>
                  <th colSpan="2" className="bg-gray-600 text-white text-left px-6 py-3 uppercase">
                    Service Fee
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-3">Processing Fee</td>
                  <td className="px-6 py-3 text-right">${processingFee.toFixed(2)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th className="bg-gray-600 text-white text-left px-6 py-3">Total</th>
                  <th className="bg-gray-600 text-white text-right px-6 py-3">${processingFee.toFixed(2)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
          )}

          {/* VIN correction: no payment, direct submit */}
          {isVinCorrection ? (
            <form
              onSubmit={(e) => { e.preventDefault(); handleSubmitVinCorrection(); }}
              className="px-6 py-4"
            >
              <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 mb-4">
                VIN corrections are free of charge. Click <b>Submit to IRS</b> to
                send your VIN correction.
              </div>

              {vinError && (
                <p className="mb-2 text-red-600 text-sm">{vinError}</p>
              )}

              <div className="flex items-start space-x-2 mt-2">
                <input
                  type="checkbox"
                  id="consent-vin"
                  className="mt-1 h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <label htmlFor="consent-vin" className="text-sm">
                  I agree to the disclosure on Form 2290 and Schedule 1 / Form 8849.{' '}
                  <a
                    href={`/company/${formId}/file-form/disclosure`}
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    Read disclosure terms
                  </a>
                </label>
              </div>

              <button
                type="submit"
                disabled={submittingVin}
                className="mt-6 bg-[#0F2C5E] text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingVin ? 'Submitting…' : 'Submit to IRS'}
              </button>
            </form>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm
                clientSecret={clientSecret}
                consent={consent}
                onConsentChange={setConsent}
                onSuccessNavigate={() => navigate(withForm(`/company/${ID}/amended-return/irs-confirmation`, formId))}
                formId={formId}
              />
            </Elements>
          )}

          {/* Back button */}
          <div className="px-6 py-4 bg-gray-50">
            <button
              onClick={handleBack}
              className="bg-gray-500 text-white px-6 py-2 hover:bg-gray-600 transition"
            >
              Back
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}